import { Component, ElementRef, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Collection } from '../../../../../support/collection/collection';
import { AlertService } from '../../../../../alerts';
import { MODAL_ACTION, ModalOptions, ModalRef, ModalService } from '../../../../../modals';
import { ToastService } from '../../../../../toast/services/toast.service';
import { ApiCollection, ApiService, Model } from '../../../../../api';
import { Observable } from 'rxjs/Observable';
import { ChassisFormHandler } from '../../../../../forms/form/classes/form-handler';
import { LoaderService } from '../../../../../support/loading/loader.service';
import { ListPluginModal } from '../list-plugin-modal/list-plugin.modal';
import { SelectForm } from '../../select-form';
import { ListPluginOptions } from '../../interfaces/list-plugin-options.interface';
import { ALERT_MESSAGES } from '../../../../../alerts/alert-messages';
import { LIST_PLUGIN_TYPE } from '../../list-plugin-type';
import { LIST_PLUGIN_MODE } from '../../list-plugin-mode';
import { trans } from '../../../../../localization';
import { ListClosingResponse } from '../../interfaces/list-closing-response.interface';
import { Buttons } from '../../../../../modals/classes/buttons';
import { QuickAdditModal } from '../../../../../quick-addit/modals/quick-addit.modal';
import { dataGet, upperCaseFirst } from '../../../../../support/helpers';
import { addFormModalDeleteButtonTo } from './../../../../../modals/add-form-modal-delete-button-to';
import { ModalResponse } from './../../../../../modals/modal-response';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'list-plugin',
    templateUrl: './list-plugin.component.html',
    styleUrls: ['./list-plugin.component.scss'],
})
export class ListPlugin implements OnInit, OnDestroy {

    /**
     * @type {ChassisFormHandler}
     */
    @Input() public form: ChassisFormHandler;

    /**
     * Observable containing the page's model instance.
     *
     * @type {Observable<any>}
     */
    @Input() public modelSubject: Observable<Model>;

    /**
     * Observable containing the page's model instance.
     *
     * @type {Observable<any>}
     */
    @Input() public model: Observable<Model>;

    /**
     * Observable containing the page's model instance.
     *
     * @type {Observable<any>}
     */
    @Input() public service: ApiService;

    /**
     * @type {ListPluginOptions}
     */
    @Input() public options: ListPluginOptions;

    /**
     * @type {ElementRef}
     */
    @ViewChild('panel') panel: ElementRef;

    @Output() loaded = new EventEmitter();

    @Output() adding = new EventEmitter();

    @Output() added = new EventEmitter();

    @Output() creating = new EventEmitter();

    @Output() created = new EventEmitter();

    @Output() editing = new EventEmitter();

    @Output() edited = new EventEmitter();

    @Output() deleting = new EventEmitter();

    @Output() deleted = new EventEmitter();

    @Output() removing = new EventEmitter();

    @Output() removed = new EventEmitter();

    /**
     * The AlertService instance.
     *
     * @type {AlertService}
     */
    protected alertService: AlertService;

    /**
     * The ModalService instance.
     *
     * @type {ModalService}
     */
    protected modalService: ModalService;

    /**
     * The ToastService instance.
     *
     * @type {ToastService}
     */
    protected toastService: ToastService;

    /**
     * The LoaderService instance.
     *
     * @type {LoaderService}
     */
    protected loaderService: LoaderService;

    public loader;

    /**
     * The collections of items.
     *
     * @type {Collection}
     */
    @Input() public items: Array<any> = [];

    /**
     * A function containing the open remove alert method
     * for passing on to child elements.
     */
    public openRemoveAlertWrapper: Function;

    /**
     * Plugin types enum to pass on to the template.
     *
     * @type {LIST_PLUGIN_TYPE}
     */
    public PLUGIN_TYPES = LIST_PLUGIN_TYPE;

    /**
     * Plugin modes enum to pass on to the template.
     *
     * @type {LIST_PLUGIN_MODE}
     */
    public PLUGIN_MODES = LIST_PLUGIN_MODE;

    /**
     * Indicates whether the model exists on init phase or not.
     */
    private modelExists: boolean;

    /**
     * The Form Handler that is used in the modal.
     */
    public modalFormHandler: ChassisFormHandler;

    /**
     * Indicates if an error occurred when loading the initial
     * items.
     *
     * @type {boolean}
     */
    public hasLoadingError = false;

    /**
     * The current pending load request.
     */
    public loadRequest: Subscription;

    protected subscription = new Subscription();

    constructor(private injector: Injector) {
        this.alertService = injector.get(AlertService);
        this.modalService = injector.get(ModalService);
        this.toastService = injector.get(ToastService);

        this.loaderService = injector.get(LoaderService);
        this.loader        = this.loaderService.create();
    }

    ngOnInit(): void {
        this.model = this.options.data.model;

        if (this.options.data.form) {
            this.form = this.options.data.form;
        }

        if (!this.options.type) {
            this.options.type = LIST_PLUGIN_TYPE.ADD_REMOVE;
        }

        if (!this.options.mode) {
            this.options.mode = LIST_PLUGIN_MODE.TABLE;
        }

        if (!this.options.quickAddit) {
            this.options.quickAddit = false;
        }

        this.setRemoveAlertWrapperMethod();
        this.init();
    }

    protected init(): void {
        this.subscription.add(
            this.model
                .pipe(
                    take(1),
                )
                .subscribe((model: Model) => {
                    this.loader.start();
                    this.modelExists = model.exists();

                    if (model.exists()) {
                        this.loadInitialItems(model).then((items) => {
                            this.loader.stop();

                            this.loaded.emit(items);
                        }, () => {
                            this.hasLoadingError = true;

                            this.loader.stop();

                            this.loaded.emit(true);
                        });
                    } else {
                        this.loader.stop();

                        this.loaded.emit(true);
                    }

                    if (this.form) {
                        this.setFormSubmitAction(model);
                    }
                })
        );
    }

    /**
     * Sets a function containing the open remove alert method
     * for passing on to child elements into the variable.
     */
    protected setRemoveAlertWrapperMethod(): void {
        this.openRemoveAlertWrapper = (item) => {
            this.openRemoveAlert(item);
        };
    }

    /**
     * Indicates if the plugin is of the type that adds items
     * to the list and * removes items from the list.
     */
    public isAddRemoveList(): boolean {
        if (typeof this.options === 'undefined') {
            return false;
        }

        return this.options.type === LIST_PLUGIN_TYPE.ADD_REMOVE;
    }

    /**
     * Indicates if the plugin is of the type that creates new
     * items and adds them to the list, edits items in the
     * list and deletes and removes items from the list.
     */
    public isCreateEditDeleteList(): boolean {
        if (typeof this.options === 'undefined') {
            return false;
        }

        return this.options.type === LIST_PLUGIN_TYPE.CREATE_EDIT_DELETE;
    }

    /**
     * Load the initial items.
     */
    protected loadInitialItems(model: Model): Promise<any> {
        return new Promise((resolve, reject) => {

            let request;
            if (this.options.serviceWith) {
                request = this.options.service.allWith(false, this.options.serviceWith);
            } else {
                request = this.options.service.all();
            }

            this.cancelRequest();

            this.loadRequest = request
                .subscribe((items: ApiCollection) => {
                    this.items.length = 0;
                    this.items.push(...items.all());

                    resolve(items);
                }, (error) => {
                    reject(error);
                });
        });
    }

    protected setFormSubmitAction(model: Model): void {
        this.form.success((data) => {
            return new Promise((resolve, reject) => {
                this.loader.start();

                // Check if the model is existed on init. If so we don't have to
                // save the items in the list and abort
                if (this.modelExists) {
                    this.loader.stop();

                    resolve(data);
                } else {
                    this.postItemsAfterModelSaved().then(() => {
                        this.loader.stop();

                        this.modelExists = true;

                        resolve(data);
                    }, (error) => {
                        this.loader.stop();

                        reject(error);
                    });
                }
            });
        });
    }

    protected postItemsAfterModelSaved(): Promise<any> {
        return new Promise((resolve, reject) => {
            const queue = this.items.filter((item) => {
                const model = this.options.service.make(item);
                return !model.exists();
            });


            // Check if the list of items is empty and
            // resolve the promise if so
            if (!queue.length) {
                resolve();
            }

            // Loop over each item in the list and save it
            queue.forEach((item: Object, index: number) => {
                const model = this.options.service.make(item);

                this.postItem(model).then(() => {
                    // Check if the current item is the last item
                    // to be saved
                    if (index === queue.length - 1) {
                        this.toastService.success('success', trans('Item attached successfully.'));

                        resolve();
                    }
                }, (error) => {
                    this.toastService.error('error', trans('Failed to attach item'));

                    reject(error);
                });
            });
        });
    }

    protected postItem(item: Model): Promise<any> {
        return new Promise((resolve, reject) => {
            const request = item.post();

            request.subscribe((saved: Model) => {
                resolve(saved);
            }, (error) => {
                reject(error);
            });
        });
    }

    public openAddModal(): void {
        if (this.disabled) {
            return;
        }

        // If errors occurred while loading the items,
        // we'll disable adding new items
        if (this.hasLoadingError) {
            return;
        }

        this.model.subscribe((model: Model) => {
            const item            = this.options.service.make();
            this.modalFormHandler = new ChassisFormHandler(this.injector, this.createForm(), item);

            // Emit on item adding event
            this.adding.emit(item);

            const data = {
                model: item,
                form: this.modalFormHandler,
                title: this.options.modal.titles && this.options.modal.titles.add ?
                    this.options.modal.titles.add : trans('Add new') + ' ' +
                    this.getEntityNameFromModel(new this.options.service.model),
                action: MODAL_ACTION.ADD,
            };

            const options = {
                classes: 'slide-top'
            };

            const modal = this.openModal(data, options);

            this.changeModalOkButtonText(modal, trans('Add'));

            modal.closing((resolve, reject, response: ListClosingResponse) => {
                if (response.action === MODAL_ACTION.ADD) {
                    const result = response.data;

                    if (model.exists()) {
                        const instance = this.options.service.make(result);

                        this.postItem(instance).then(() => {
                            resolve(response);
                        }, (error) => {
                            this.toastService.error('error', trans('Failed to add item'));

                            reject(error);
                        });
                    } else {
                        resolve(response);
                    }
                }
            });

            modal.closed((response: ListClosingResponse) => {
                if (response.action === MODAL_ACTION.ADD) {
                    const instance = response.data;

                    this.addItemToItemList(instance);

                    // Emit on item added event
                    this.added.emit(item);

                    this.toastService.success('saved', trans('Item added successfully'));
                }
            });
        });
    }

    public openCreateModal(): any {
        if (this.disabled) {
            return;
        }

        // If errors occurred while loading the items,
        // we'll disable creating new items
        if (this.hasLoadingError) {
            return;
        }

        if (this.options.actions && this.options.actions.create && this.options.actions.create.callback) {
            return this.options.actions.create.callback();
        }

        const item = (this.options.quickAddit
            && this.options.quickAddit.entityDefaults)
            ? this.options.quickAddit.entityDefaults
            : this.options.service.make();

        if (this.options.quickAddit) {
            return this.openQuickAddItCreateModal(item);
        }

        this.modalFormHandler = new ChassisFormHandler(this.injector, this.createForm(), item);

        // Emit on item creating event
        this.creating.emit(item);

        const data = {
            model: item,
            form: this.modalFormHandler,
            title: this.options.modal.titles && this.options.modal.titles.create ?
                this.options.modal.titles.create : trans('Create new') + ' ' +
                this.getEntityNameFromModel(new this.options.service.model),
            action: MODAL_ACTION.CREATE,
        };

        const closing = (model: Model, response: ListClosingResponse, res, rej) => {
            return new Promise((resolve, reject) => {
                if (response.action === MODAL_ACTION.CREATE) {
                    const result = response.data;

                    if (model.exists()) {
                        const instance = this.options.modal.form.service.make(result);

                        this.saveItem(instance).then((result) => {
                            response.data = result;

                            resolve(response);
                            res(response);
                        }, (error) => {
                            this.toastService.error('error', trans('Failed to create item'));

                            rej(error);
                            reject(error);
                        });
                    } else {
                        res(response);
                        resolve(response);
                    }
                }
            });
        };

        const closed = (model: Model, response: ListClosingResponse) => {
            if (response.action === MODAL_ACTION.CREATE) {
                const instance = response.data;

                this.addCreatedItemToItemsList(instance);
            }
        };

        this.openCreateOrEditModal(data, {}, closing, closed).then((modal: ModalRef) => {
            this.changeModalOkButtonText(modal, 'Create');
        });
    }

    /**
     * Open a quick add it modal.
     *
     * @param item - The item to use as the default value.
     * @param action - Indicates if it is a create or edit action on the item.
     */
    protected openQuickAddItModal(item: { id: number | string, [key: string]: any }, action: 'create' | 'edit'): ModalRef {
        const entity = this.options.quickAddit
            ? this.options.quickAddit.entity
            : null;

        const service = this.options.quickAddit
            ? this.options.quickAddit.service
            : null;

        const modal = this.modalService.open(
            QuickAdditModal,
            {
                entity: entity,
                entityId: item.id,
                entityDefaults: item,
                service: service,
                action: action,
                parent: this.model
            }
        );

        return modal;
    }

    /**
     * Open a quick add it modal in creation mode.
     *
     * @param item - The item to use as the default value.
     */
    protected openQuickAddItCreateModal(item: { id: number | string, [key: string]: any }): void {
        const modal = this.openQuickAddItModal(item, 'create');

        modal.closing((resolve, reject, response: any) => {
            this.parseQuickAddItModel(response.model)
                .subscribe((model) => {
                    resolve(model);
                }, (error) => {
                    reject(error);
                });
        });

        modal.closed((model: Model) => {
            this.addItemToItemList(model);

            this.created.next(model);
        });
    }

    /**
     * Open a quick add it modal in edit mode.
     *
     * @param item - The item to use as the default value.
     */
    protected openQuickAddItEditModal(item: { id: number | string, [key: string]: any }): void {
        const modal = this.openQuickAddItModal(item, 'edit');

        addFormModalDeleteButtonTo(this.injector, modal);

        modal.closing((resolve, reject, response) => {
            switch (response.action) {
                case 'delete':
                    response.action = MODAL_ACTION.DELETE
                    break;

                case 'edit':
                    response.action = MODAL_ACTION.EDIT
                    break;
            }

            switch (response.action) {
                case MODAL_ACTION.DELETE:
                    this.parseQuickAddItModel(item)
                        .subscribe((model) => {
                            resolve({
                                action: MODAL_ACTION.DELETE,
                                model
                            });
                        }, (error) => {
                            reject(error);
                        });
                    break;

                case MODAL_ACTION.EDIT:
                    this.parseQuickAddItModel(response.model)
                        .subscribe((model) => {
                            resolve({
                                action: MODAL_ACTION.EDIT,
                                model
                            });
                        }, (error) => {
                            reject(error);
                        });
            }

            modal.closed((response: { action: MODAL_ACTION, model: Model }) => {
                const model = response.model;

                switch (response.action) {
                    case MODAL_ACTION.EDIT:
                        this.updateItemInItemList(
                            model,
                            model
                        );

                        this.edited.next(model);
                        break;

                    case MODAL_ACTION.DELETE:
                        this.removeItemFromItemList(model);

                        this.deleted.next(model);
                        break;
                }
            });
        });
    }

    /**
     * Create a model instance from the `data`.
     *
     * @param data
     */
    protected parseQuickAddItModel<T>(data: { [key: string]: any }): Observable<T> {
        return new Observable((observer) => {
            const service = this.options.quickAddit
                ? this.options.quickAddit.service
                : null;

            if (!service) {
                observer.error('No quick add it service provided');
            }

            const model = service.make(data);

            observer.next(model);
            observer.complete();
        });
    }

    protected addCreatedItemToItemsList(item: { [key: string]: any }): void {
        this.addItemToItemList(item);

        // Emit on item created event
        this.created.emit(item);

        this.toastService.success('saved', trans('Item created successfully'));
    }

    public openEditModalIfIsCreateEditDeleteList(item) {
        if (this.disabled) {
            return;
        }

        if (this.options.actions && this.options.actions.edit && this.options.actions.edit.callback) {
            return this.options.actions.edit.callback(item);
        }

        if (this.isCreateEditDeleteList()) {
            this.openEditModal(item);
        }
    }

    public openEditModal(item): void {
        if (this.options.quickAddit) {
            return this.openQuickAddItEditModal(item);
        }

        this.modalFormHandler = new ChassisFormHandler(this.injector, this.createForm(), item);

        // Emit on item editing event
        this.editing.emit(item);

        const data = {
            model: item,
            form: this.modalFormHandler,
            title: this.options.modal.titles && this.options.modal.titles.edit ?
                this.options.modal.titles.edit : trans('Edit') + ' ' +
                this.getEntityNameFromModel(new this.options.service.model),
            action: MODAL_ACTION.EDIT,
        };

        const closing = (model: Model, response: ModalResponse, res, rej) => {
            return new Promise((resolve, reject) => {
                if (response.action === MODAL_ACTION.EDIT) {
                    const result = response.data;

                    if (model.exists()) {
                        item = this.options.modal.form.service.make(result);

                        this.saveItem(item).then((result) => {
                            response.data = result;

                            res(response);
                            resolve(response);
                        }, (error) => {
                            this.toastService.error('error', trans('Failed to update item'));

                            rej(error);
                            reject(error);
                        });
                    } else {
                        res(response);
                        resolve(response);
                    }
                } else if (response.action === MODAL_ACTION.DELETE) {
                    if (model.exists()) {
                        // Emit on item deleting event
                        this.deleting.emit(item);

                        this.deleteItem(item).then((result) => {
                            res(response);
                            resolve(response);
                        }, (error) => {
                            this.toastService.error('error', trans('Failed to delete item'));

                            rej(error);
                            reject(error);
                        });
                    }
                }
            });
        };

        const closed = (model: Model, response: ModalResponse) => {
            return new Promise((resolve, reject) => {
                if (response.action === MODAL_ACTION.EDIT) {
                    const editedItem = response.data;

                    this.updateItemInItemList(item, editedItem);

                    // Emit on item edited event
                    this.edited.emit(editedItem);

                    this.toastService.success('saved', trans('Item updated successfully'));

                    resolve();
                }

                if (response.action === MODAL_ACTION.DELETE) {
                    this.removeItemFromItemList(item).then(() => {
                        // Emit on item deleted event
                        this.deleted.emit(item);

                        this.toastService.success('saved', trans('Item deleted successfully'));

                        resolve();
                    }, (error) => {
                        reject(error);
                    });
                }
            });
        };

        this.openCreateOrEditModal(data, {}, closing, closed).then((modal: ModalRef) => {
            this.addModalDeleteButton(modal);
        });
    }

    private addModalDeleteButton(modal: ModalRef): void {
        const buttons: Buttons = modal.instance.modal.get('buttons');

        buttons.after('close', 'delete', {
            text: trans('Delete'),
            callback: (item) => {
                const modalRef = modal.instance.modal;
                const alerts   = this.injector.get(AlertService);

                const prompt = alerts.open({
                    title: trans(ALERT_MESSAGES.ARE_YOU_SURE),
                });

                prompt.result.then(() => {
                    modalRef.close({
                        action: MODAL_ACTION.DELETE,
                        data: item,
                    });
                }, () => {
                });
            }
        });
    }

    public openCreateOrEditModal(data, options: ModalOptions = {}, closing, closed): Promise<ModalRef> {
        return new Promise((resolve, reject) => {
            this.model.subscribe((model: Model) => {
                const modal = this.openModal(data, options);

                resolve(modal);

                modal.closing((res, rej, result) => {
                    closing(model, result, res, rej);
                });

                modal.closed((result: Model) => {
                    closed(model, result);
                });
            });
        });
    }

    public openModal(data: { [key: string]: any } = {}, options?: ModalOptions): ModalRef {
        return this.modalService.open(ListPluginModal, data, options);
    }

    protected saveItem(item: Model): Promise<any> {
        return new Promise((resolve, reject) => {
            const request = item.save();

            request.subscribe((saved: Model) => {
                resolve(saved);
            }, (error) => {
                reject(error);
            });
        });
    }

    protected deleteItem(item: Model): Promise<any> {
        return new Promise((resolve, reject) => {
            if (this.options.modal.form.services && this.options.modal.form.services.delete) {

                item = this.options.modal.form.services.delete.make(item.plain());
            }

            const request = item.delete();

            request.subscribe((saved: Model) => {
                resolve(saved);
            }, (error) => {
                reject(error);
            });
        });
    }

    protected changeModalOkButtonText(modal: ModalRef, text: string): void {
        const buttons = modal.instance.modal.get('buttons');
        const ok      = buttons.get('ok');
        ok.text       = text;
    }

    protected createForm(): any {
        if (this.options.modal.form.structure) {
            return this.options.modal.form.structure;
        }

        if (this.isAddRemoveList()) {
            return SelectForm({
                url: this.options.modal.form.service.all(true),
                label: this.options.modal.form.element.name,
                searchDisplayColumn: (this.options.modal.form.element.search.display || null),
                searchQueryColumn: (this.options.modal.form.element.search.query || null),
                searchKeyColumn: (this.options.modal.form.element.search.key || null),
            });
        } else {
            return this.options.modal.form.service.entityName;
        }
    }

    /**
     * Add an item to the list of items.
     *
     * @param item - The item to add.
     */
    protected addItemToItemList<T>(item: T): Array<T> {
        this.items.unshift(item);
        return this.items;
    }

    /**
     * Update the `item` in the item list to the `replacement`.
     *
     * @param item
     * @param replacement
     */
    protected updateItemInItemList<T extends { id: number, uid: string }>(item: T, replacement: T): Array<T> {
        this.items.splice(
            this.items.findIndex((i) => {
                return item.uid === i.uid;
            }),
            1,
            replacement
        );

        return this.items;
    }

    public openRemoveAlert(item): void {
        if (this.disabled) {
            return;
        }

        // Emit on item removing event
        this.removing.emit(item);

        const prompt = this.alertService.open({
            title: ALERT_MESSAGES.ARE_YOU_SURE,
        });

        prompt.closing((resolve, reject) => {
            this.model.subscribe((model: Model) => {
                if (model.exists()) {
                    this.removeItem(item).then(() => {
                        resolve(item);
                    }, (error) => {
                        this.toastService.error('error', trans('Failed to remove item'));

                        reject(error);
                    });
                } else {
                    resolve(item);
                }
            });
        });

        prompt.closed((result) => {
            this.removeItemFromItemList(result).then(() => {
                // Emit on item removed event
                this.removed.emit(result);

                this.toastService.success('removed', trans('Item removed successfully'));
            }, () => {
            });
        });
    }

    protected removeItem(item: Model): Promise<any> {
        return new Promise((resolve, reject) => {
            this.options.service.delete(item.id).subscribe(() => {
                resolve();
            }, (error) => {
                reject(error);
            });
        });
    }

    /**
     * Remove an item from the item list.
     *
     * @param item - The item to remove from the item list.
     */
    protected removeItemFromItemList<T extends { id: number, uid: string }>(item: T): Promise<Array<T>> {
        return new Promise((resolve) => {
            this.items.splice(
                this.items.findIndex((i) => {
                    return item.uid === i.uid;
                }),
                1
            );

            resolve(this.items);
        });
    }

    protected getEntityNameFromModel(model: Model): string {
        return upperCaseFirst(
            model.constructor.name
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, function (str) {
                    return str.toUpperCase();
                })
                .toLowerCase()
                .replace(' model', '')
        );
    }

    public getTableValue(row, column: { name: string }): any {
        row = this.getTransformedData(row);

        return dataGet(row, column.name);
    }

    public getTransformedData(data): any {
        if (this.options.transformer) {
            return this.options.transformer(data);
        }

        return data;
    }

    /**
     * Indicates if the plugin is disabled.
     *
     * @return {boolean}
     */
    public get disabled(): boolean {
        return this.options && this.options.disabled ? typeof this.options.disabled === 'function' ?
            this.options.disabled() : this.options.disabled : false;
    }

    public get panelClasses(): string | Object {
        let classes = (this.options && this.options.classes ? (typeof this.options.classes === 'function' ?
            this.options.classes() : this.options.classes) : '');

        classes = typeof classes === 'string' ? classes.concat(this.disabled ? 'disabled' : '') :
            Object.assign(classes, {
                'disabled': this.disabled
            });

        return classes;
    }

    protected cancelRequest(): void {
        if (this.loadRequest) {
            this.loadRequest.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        this.cancelRequest();
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/plugins/abstract-plugins/list/components/list-plugin/list-plugin.component.ts